import React from "react";
import { Button } from "@zyno/ui";
import { Card, CardHeader, CardContent, CardFooter } from "@zyno/ui";
import { AlertCircle, RefreshCw } from "lucide-react";
import { handleSignOut } from "@/lib/actions/auth";

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  withSignOut?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
  withSignOut: boolean;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      withSignOut: props.withSignOut || false,
    };
  }

  static getDerivedStateFromError(error: Error, prevState?: State): State {
    return {
      hasError: true,
      error,
      withSignOut: prevState?.withSignOut || false,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Error caught by boundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex w-full items-center justify-center min-h-screen p-4">
          <Card className="w-full max-w-md border-none">
            <CardHeader className="flex flex-col items-center gap-2">
              <AlertCircle className="h-12 w-12 text-destructive" />
              <h2 className="text-2xl font-bold text-center">Algo salió mal</h2>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground text-center">
                {this.state.error?.message || "Ha ocurrido un error inesperado"}
              </p>
            </CardContent>
            <CardFooter className="flex justify-center gap-2">
              <Button
                onClick={() => {
                  this.setState({ hasError: false, error: null });
                  window.location.reload();
                }}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Recargar página
              </Button>
              {this.props.withSignOut && (
                <Button
                  variant="destructive"
                  onClick={async () => {
                    await handleSignOut();
                  }}
                >
                  Cerrar sesión
                </Button>
              )}
            </CardFooter>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
